import React, { useEffect, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, message, Modal, Popconfirm, Row, Select, Space, Table, Tag, Divider, Tooltip } from 'antd';
import dayjs from 'dayjs';
import api from '../../api/http';

const MAX_POINTS = 12;
const MAX_CONTRIBUTION = 6;
const POINT_STEP = 0.5;

const GROUP_LABEL = {
  three_violation: '三违',
  red_line: '安全红线',
  life_clause: '生命条款'
};

const clamp = (value, min, max) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};

const SafetyPointsAnnualPage = ({ permissions = [] }) => {
  const canManage = permissions.includes('safety_points_manage');
  const canView = canManage || permissions.includes('safety_points_view');

  const [year, setYear] = useState(dayjs().year());
  const [nameQuery, setNameQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [records, setRecords] = useState([]);

  const [typesLoading, setTypesLoading] = useState(false);
  const [violationTypes, setViolationTypes] = useState([]);

  const [contribLoading, setContribLoading] = useState(false);
  const [contribRecords, setContribRecords] = useState([]);

  const [contribPresetsLoading, setContribPresetsLoading] = useState(false);
  const [contribPresets, setContribPresets] = useState([]);

  const [deductionModalOpen, setDeductionModalOpen] = useState(false);
  const [contribModalOpen, setContribModalOpen] = useState(false);

  const [form] = Form.useForm();
  const [contribForm] = Form.useForm();

  const stepDeductPoints = (delta) => {
    const current = Number(form.getFieldValue('deduct_points')) || 0;
    const next = clamp(Number((current + delta).toFixed(2)), 0, MAX_POINTS);
    form.setFieldsValue({ deduct_points: next });
  };

  const stepContributionPoints = (delta) => {
    const current = Number(contribForm.getFieldValue('points')) || 0;
    const next = clamp(Number((current + delta).toFixed(2)), 0.01, MAX_CONTRIBUTION);
    contribForm.setFieldsValue({ points: next });
  };

  const loadEmployees = async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {
        status: '在职'
      };
      if (String(nameQuery || '').trim()) params.name = String(nameQuery).trim();

      const { data } = await api.get('/employees', { params });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadRecords = async (employeeName) => {
    setRecordsLoading(true);
    try {
      const { data } = await api.get('/safety-points/annual/records', { params: { year, employee_name: employeeName } });
      setRecords(data?.records || []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载扣分记录失败');
    } finally {
      setRecordsLoading(false);
    }
  };

  const loadViolationTypes = async () => {
    setTypesLoading(true);
    try {
      const { data } = await api.get('/safety-points/annual/violation-types');
      setViolationTypes(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载违章预设失败');
    } finally {
      setTypesLoading(false);
    }
  };

  const loadContrib = async (employeeName) => {
    setContribLoading(true);
    try {
      const { data } = await api.get('/safety-points/annual/contributions', { params: { year, employee_name: employeeName } });
      setContribRecords(data?.records || []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载贡献积分失败');
    } finally {
      setContribLoading(false);
    }
  };

  const loadContribPresets = async () => {
    setContribPresetsLoading(true);
    try {
      const { data } = await api.get('/safety-points/annual/contribution-presets');
      setContribPresets(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '加载贡献积分预设失败');
    } finally {
      setContribPresetsLoading(false);
    }
  };

  useEffect(() => {
    loadViolationTypes();
    loadContribPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, canView]);

  useEffect(() => {
    if (!canView) return;
    const t = setTimeout(() => {
      loadEmployees();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, nameQuery]);

  const openEmployee = async (row) => {
    setDetailEmployee(row);
    setDetailOpen(true);
    await loadRecords(row.name);
    await loadContrib(row.name);
  };

  const openDeductionModal = () => {
    if(!detailEmployee) return;
    form.resetFields();
    form.setFieldsValue({
      violation_date: dayjs(),
      violation_time: dayjs(),
      violation_group: 'three_violation',
      deduct_points: 1
    });
    setDeductionModalOpen(true);
  };

  const openContribModal = () => {
    if(!detailEmployee) return;
    contribForm.resetFields();
    contribForm.setFieldsValue({
      contribution_time: dayjs(),
      points: 1
    });
    setContribModalOpen(true);
  };

  const submitDeduction = async () => {
    try {
      const v = await form.validateFields();
      const mediaUrls = v.media_urls
        ? String(v.media_urls).split(/\r?\n/).map(s => s.trim()).filter(Boolean)
        : null;
      const payload = {
        employee_name: detailEmployee.name,
        violation_time: v.violation_time ? v.violation_time.format('YYYY-MM-DD HH:mm:ss') : null,
        violation_date: v.violation_date.format('YYYY-MM-DD'),
        violation_group: v.violation_group,
        violation_type_id: v.violation_type_id || null,
        violation_name: v.violation_name || null,
        location: v.location || null,
        content: v.content || null,
        media_urls: mediaUrls,
        suggestion: v.suggestion || null,
        fine_text: v.fine_text || null,
        deduct_points: v.deduct_points,
        remark: v.remark || null,
      };
      await api.post('/safety-points/annual/records', payload);
      message.success('已扣分');
      setDeductionModalOpen(false);
      form.resetFields();
      await loadRecords(detailEmployee.name);
      await loadEmployees();
    } catch (e) {
      if (!e?.errorFields) message.error(e.response?.data?.error || e.message || '扣分失败');
    }
  };

  const submitContribution = async () => {
    try {
      const v = await contribForm.validateFields();
      const mediaUrls = v.media_urls
        ? String(v.media_urls).split(/\r?\n/).map(s => s.trim()).filter(Boolean)
        : null;
      const payload = {
        employee_name: detailEmployee.name,
        contribution_time: v.contribution_time ? v.contribution_time.format('YYYY-MM-DD HH:mm:ss') : null,
        points: v.points,
        description: v.description || null,
        media_urls: mediaUrls,
        remark: v.remark || null,
      };
      await api.post('/safety-points/annual/contributions', payload);
      message.success('已增加贡献积分');
      setContribModalOpen(false);
      contribForm.resetFields();
      await loadContrib(detailEmployee.name);
      await loadEmployees();
    } catch (e) {
      if (!e?.errorFields) message.error(e.response?.data?.error || e.message || '新增贡献积分失败');
    }
  };

  const deleteContribution = async (id) => {
    try {
      await api.delete('/safety-points/annual/contributions/' + id);
      message.success('已删除');
      await loadContrib(detailEmployee.name);
      await loadEmployees();
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '删除失败');
    }
  };

  const deleteRecord = async (id) => {
    try {
      await api.delete('/safety-points/annual/records/' + id);
      message.success('已删除');
      await loadRecords(detailEmployee.name);
      await loadEmployees();
    } catch (e) {
      message.error(e.response?.data?.error || e.message || '删除失败');
    }
  };

  const recordColumns = [
    { title: '日期', dataIndex: 'violation_date', width: 110 },
    { title: '分类', dataIndex: 'violation_group', width: 100, render: v => <Tag>{GROUP_LABEL[v] || v || '-'}</Tag> },
    { title: '违章行为', dataIndex: 'violation_name' },
    { title: '扣分', dataIndex: 'deduct_points', width: 90, render: v => <Tag color="red">-{(Number(v)||0).toFixed(2)}</Tag> },
    { title: '处罚标准', dataIndex: 'fine_text', width: 140, render: v => v || '-' },
    { title: '备注', dataIndex: 'remark', width: 180, render: v => v || '-' },
    { title: '录入人', dataIndex: 'created_by_user_name', width: 120, render: v => v || '-' },
    {
      title: '操作',
      width: 90,
      render: (_, r) => canManage ? (
        <Popconfirm title="确认删除这条扣分记录？" onConfirm={() => deleteRecord(r.id)}>
          <Button size="small" danger>删除</Button>
        </Popconfirm>
      ) : '-'
    }
  ];

  const contribColumns = [
    { title: '时间', dataIndex: 'contribution_time', width: 150, render: v => v || '-' },
    { title: '积分', dataIndex: 'points', width: 90, render: v => <Tag color="green">+{(Number(v)||0).toFixed(2)}</Tag> },
    { title: '说明', dataIndex: 'description', render: v => v || '-' },
    { title: '备注', dataIndex: 'remark', width: 160, render: v => v || '-' },
    {
      title: '操作',
      width: 90,
      render: (_, r) => canManage ? (
        <Popconfirm title="确认删除这条贡献积分记录？" onConfirm={() => deleteContribution(r.id)}>
          <Button size="small" danger>删除</Button>
        </Popconfirm>
      ) : '-'
    }
  ];

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <h2 style={{ marginBottom: 8 }}>安全积分</h2>
        <div style={{ color: '#666' }}>无权限访问</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 8 }}>安全积分</h2>
      <p style={{ color: '#666', marginBottom: 16 }}>员工行为积分管理（扣分/贡献积分录入）。</p>

      <Space style={{ marginBottom: 16 }} wrap>
        <DatePicker
          picker="year"
          value={dayjs(String(year), 'YYYY')}
          onChange={(v) => setYear(v ? v.year() : dayjs().year())}
        />
        <Input
          placeholder="按姓名检索"
          allowClear
          value={nameQuery}
          onChange={(e)=> setNameQuery(e.target.value)}
          style={{ width: 220 }}
        />
        <Button
          onClick={() => {
            setNameQuery('');
            setDetailEmployee(null);
          }}
          disabled={loading}
        >
          清空筛选
        </Button>
        <Button onClick={loadEmployees} loading={loading}>刷新</Button>
        <Select
          allowClear
          showSearch
          placeholder="选择员工后可扣分/奖励"
          style={{ width: 240 }}
          value={detailEmployee?.id || undefined}
          options={list.map(emp => ({
            value: emp.id,
            label: `${emp.name}${emp.department || emp.section ? `（${emp.department || emp.section}）` : ''}`
          }))}
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          onChange={(val) => {
            const picked = list.find(emp => Number(emp.id) === Number(val));
            setDetailEmployee(picked || null);
          }}
        />
        <Tooltip title={!canManage ? '无权限：需要 safety_points_manage' : (!detailEmployee ? '请先选择员工' : '')}>
          <Button type="primary" danger onClick={openDeductionModal} disabled={!canManage || !detailEmployee}>扣分弹窗</Button>
        </Tooltip>
        <Tooltip title={!canManage ? '无权限：需要 safety_points_manage' : (!detailEmployee ? '请先选择员工' : '')}>
          <Button onClick={openContribModal} disabled={!canManage || !detailEmployee}>奖励弹窗</Button>
        </Tooltip>
      </Space>

      <Row gutter={[12, 12]}>
        {list.map((r, idx) => {
          return (
            <Col key={`${r.name || 'unknown'}_${idx}`} xs={24} sm={12} md={8} lg={6}>
              <Card
                size="small"
                hoverable
                onClick={() => openEmployee(r)}
                title={<span>{r.name} <span style={{ color: '#999', fontWeight: 'normal' }}>{r.department || r.section || ''}</span></span>}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666' }}>
                    <div>岗位：{r.position || '-'}</div>
                    <div>状态：{r.status || '-'}</div>
                  </div>
                  <Button size="small" onClick={(e) => { e.stopPropagation(); openEmployee(r); }}>查看明细</Button>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal
        title={detailEmployee ? `积分明细 - ${detailEmployee.name}（${year}年）` : '积分明细'}
        open={detailOpen}
        width={920}
        footer={null}
        destroyOnClose
        onCancel={() => {
          setDetailOpen(false);
          setDetailEmployee(null);
          setRecords([]);
          setContribRecords([]);
          setDeductionModalOpen(false);
          setContribModalOpen(false);
          form.resetFields();
          contribForm.resetFields();
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {detailEmployee && (
            <Space wrap>
              <Tooltip title={canManage ? '' : '无权限：需要 safety_points_manage'}>
                <Button type="primary" onClick={openDeductionModal} disabled={!canManage}>新增违章行为（扣分）</Button>
              </Tooltip>
              <Tooltip title={canManage ? '' : '无权限：需要 safety_points_manage'}>
                <Button onClick={openContribModal} disabled={!canManage}>新增安全贡献积分</Button>
              </Tooltip>
            </Space>
          )}

          <Table
            rowKey="id"
            size="small"
            loading={recordsLoading}
            columns={recordColumns}
            dataSource={records}
            pagination={false}
          />

          <Divider orientation="left">安全贡献积分（年度最多 {MAX_CONTRIBUTION} 分，仅冲减“三违”扣分）</Divider>
          <Table
            rowKey="id"
            size="small"
            loading={contribLoading}
            columns={contribColumns}
            dataSource={contribRecords}
            pagination={false}
          />
        </Space>
      </Modal>

      {/* 新增违章行为（扣分） Modal */}
      <Modal
        title={detailEmployee ? `新增违章行为（扣分） - ${detailEmployee.name}` : '新增违章行为（扣分）'}
        open={deductionModalOpen}
        onOk={submitDeduction}
        onCancel={() => { setDeductionModalOpen(false); form.resetFields(); }}
        okText="提交"
        cancelText="取消"
        destroyOnClose
        width={720}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            violation_date: dayjs(),
            violation_time: dayjs(),
            violation_group: 'three_violation',
            deduct_points: 1
          }}
        >
          <Form.Item name="violation_time" label="违章时间">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="violation_date" label="违章日期" rules={[{ required: true, message: '请选择日期' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="violation_group" label="违章类别" rules={[{ required: true }]}>
            <Select
              options={[
                { label: '一般“三违”行为', value: 'three_violation' },
                { label: '安全红线条款', value: 'red_line' },
                { label: '违反生命条款', value: 'life_clause' },
              ]}
            />
          </Form.Item>

          <Form.Item name="violation_type_id" label="违章行为(预设)">
            <Select
              allowClear
              showSearch
              loading={typesLoading}
              placeholder="可从预设条款选择，自动带出扣分与处罚标准"
              options={violationTypes.map(t => ({
                label: `${GROUP_LABEL[t.group_name] || t.group_name}-${t.seq} ${t.title}`,
                value: t.id,
              }))}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              onChange={(val) => {
                const t = violationTypes.find(x => x.id === val);
                if(!t) return;
                const fixed = t.deduct_min !== null && t.deduct_max !== null && Number(t.deduct_min) === Number(t.deduct_max);
                form.setFieldsValue({
                  violation_group: t.group_name,
                  violation_name: t.title,
                  fine_text: t.fine_text || null,
                  deduct_points: fixed ? Number(t.deduct_min) : form.getFieldValue('deduct_points')
                });
              }}
            />
          </Form.Item>

          <Form.Item name="violation_name" label="违章行为(可手填)" rules={[{ required: true, message: '请输入违章行为' }]}>
            <Input placeholder="例如：未佩戴安全帽" />
          </Form.Item>

          <Form.Item name="location" label="地点">
            <Input placeholder="例如：1#厂房东侧通道" />
          </Form.Item>

          <Form.Item name="content" label="内容描述">
            <Input.TextArea rows={2} placeholder="可填写违章经过/现场情况" />
          </Form.Item>

          <Form.Item name="media_urls" label="照片/视频（URL，一行一个）">
            <Input.TextArea rows={2} placeholder="https://...\nhttps://..." />
          </Form.Item>

          <Form.Item name="suggestion" label="处理建议">
            <Input placeholder="例如：现场整改+安全教育" />
          </Form.Item>

          <Form.Item name="fine_text" label="经济处罚标准">
            <Input placeholder="例如：300 元/人·次" />
          </Form.Item>

          <Form.Item name="deduct_points" label="扣分" rules={[{ required: true, message: '请输入扣分' }]}>
            <Space.Compact style={{ width: '100%' }}>
              <Button onClick={() => stepDeductPoints(-POINT_STEP)}>-</Button>
              <InputNumber min={0} max={MAX_POINTS} precision={2} step={POINT_STEP} style={{ width: '100%' }} />
              <Button onClick={() => stepDeductPoints(POINT_STEP)}>+</Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增安全贡献积分 Modal */}
      <Modal
        title={detailEmployee ? `新增安全贡献积分 - ${detailEmployee.name}` : '新增安全贡献积分'}
        open={contribModalOpen}
        onOk={submitContribution}
        onCancel={() => { setContribModalOpen(false); contribForm.resetFields(); }}
        okText="提交"
        cancelText="取消"
        destroyOnClose
        width={640}
      >
        <Form
          form={contribForm}
          layout="vertical"
          initialValues={{ contribution_time: dayjs(), points: 1 }}
        >
          <Form.Item name="contribution_time" label="时间">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="preset_id" label="贡献积分行为(预设)">
            <Select
              allowClear
              showSearch
              loading={contribPresetsLoading}
              placeholder="选择预设可自动带出积分与说明"
              options={contribPresets.map(p => ({
                label: `${p.seq}. ${p.title}`,
                value: p.id,
              }))}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              onChange={(val) => {
                const p = contribPresets.find(x => x.id === val);
                if(!p) return;
                contribForm.setFieldsValue({
                  description: p.title,
                  points: (p.default_points !== null && p.default_points !== undefined) ? Number(p.default_points) : contribForm.getFieldValue('points')
                });
              }}
            />
          </Form.Item>

          <Form.Item name="points" label={`贡献积分（年度累计不超过 ${MAX_CONTRIBUTION}）`} rules={[{ required: true, message: '请输入积分' }]}>
            <Space.Compact style={{ width: '100%' }}>
              <Button onClick={() => stepContributionPoints(-POINT_STEP)}>-</Button>
              <InputNumber min={0.01} max={MAX_CONTRIBUTION} precision={2} step={POINT_STEP} style={{ width: '100%' }} />
              <Button onClick={() => stepContributionPoints(POINT_STEP)}>+</Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item name="description" label="说明">
            <Input placeholder="例如：参与隐患排查并有效整改" />
          </Form.Item>

          <Form.Item name="media_urls" label="照片/视频（URL，一行一个）">
            <Input.TextArea rows={2} placeholder="https://...\nhttps://..." />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SafetyPointsAnnualPage;
